import React, { useContext, useState } from 'react';
import { signOut } from 'aws-amplify/auth';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  AndroidOutlined,
  WifiOutlined,
  FundProjectionScreenOutlined,
  CompassOutlined,
  AlertOutlined,
  SafetyOutlined,
  BarChartOutlined,
  WarningOutlined,
  IdcardOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { IoCarSport } from 'react-icons/io5';
import {
  Button,
  Drawer,
  Grid,
  Layout,
  Menu,
  Tag,
  theme,
  Tooltip,
  Typography,
  message,
} from 'antd';
import Settings from './Settings';
import { SimulatorContext, DispatchContext, ACTIONS } from 'store/context';
import { connect_websocket, subscribeToShadowDelta } from 'mqtt/aws';

const { Header, Sider, Content, Footer } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const BrandMark = ({ collapsed }) => (
  <div
    style={{
      height: 64,
      display: 'flex',
      alignItems: 'center',
      padding: collapsed ? '0 23px' : '0 18px',
      borderBottom: '1px solid #e2e8f0',
      overflow: 'hidden',
      gap: 10,
      flexShrink: 0,
    }}
  >
    <div
      style={{
        width: 34,
        height: 34,
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        borderRadius: 9,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <IoCarSport style={{ color: '#fff', fontSize: 18 }} />
    </div>
    {!collapsed && (
      <div style={{ overflow: 'hidden' }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: '#0f172a',
            lineHeight: '16px',
            whiteSpace: 'nowrap',
          }}
        >
          Cloud Simulator
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: '15px' }}>
          Telematics Portal
        </div>
      </div>
    )}
  </div>
);

const StatusBadge = ({ connected }) => (
  <Tag
    color={connected ? 'success' : 'error'}
    style={{
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 500,
      padding: '2px 10px',
      display: 'flex',
      alignItems: 'center',
      gap: 5,
      lineHeight: '18px',
    }}
  >
    <span
      style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        background: connected ? '#10b981' : '#ef4444',
        display: 'inline-block',
        boxShadow: connected
          ? '0 0 0 2px rgba(16,185,129,0.25)'
          : '0 0 0 2px rgba(239,68,68,0.25)',
      }}
    />
    {connected ? 'Connected' : 'Disconnected'}
  </Tag>
);

const menuItems = [
  {
    type: 'group',
    label: 'OVERVIEW',
    children: [
      { key: '/', icon: <BarChartOutlined />, label: 'Dashboard' },
      { key: '/vehicle-overview', icon: <IoCarSport />, label: 'Vehicle Overview' },
      { key: '/dtc-signals', icon: <WarningOutlined />, label: 'DTC Signals' },
    ],
  },
  {
    type: 'group',
    label: 'MONITORING',
    children: [
      { key: '/alerts', icon: <AlertOutlined />, label: 'Alerts' },
      {
        key: '/live-logs-simulator',
        icon: <FundProjectionScreenOutlined />,
        label: 'Live Logs',
      },
    ],
  },
  {
    type: 'group',
    label: 'CONTROL',
    children: [
      { key: '/remote-functions', icon: <WifiOutlined />, label: 'Remote Functions' },
      { key: '/valet-mode', icon: <IdcardOutlined />, label: 'Valet Mode' },
    ],
  },
  {
    type: 'group',
    label: 'LOCATION',
    children: [
      {
        key: '/location-based-services',
        icon: <CompassOutlined />,
        label: 'Location Services',
      },
    ],
  },
  {
    type: 'group',
    label: 'TOOLS',
    children: [
      { key: '/android-test-client', icon: <AndroidOutlined />, label: 'Test Client' },
      { key: '/safety', icon: <SafetyOutlined />, label: 'Safety' },
    ],
  },
  { type: 'divider' },
  {
    key: '/logout',
    icon: <LogoutOutlined />,
    label: 'Sign Out',
    danger: true,
    onClick: async () => {
      await signOut({ global: true });
    },
  },
];

const Navigation = ({ user, children }) => {
  const screens = useBreakpoint();
  const isMobile = screens.md === false;

  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const {
    vehicleSimulatorSettings: { serialNo = '' },
    stsToken: credentials,
  } = useContext(SimulatorContext);
  const { accessKeyId } = credentials;
  const dispatch = useContext(DispatchContext);

  React.useEffect(() => {
    let connection;
    const initValetMode = async () => {
      try {
        connection = await connect_websocket(credentials, 'valet_global');
        await subscribeToShadowDelta(connection, serialNo, (data) => {
          if (data.state && data.state.valetMode) {
            dispatch({ type: ACTIONS.UPDATE_VALET_MODE, payload: data.state.valetMode });
            message.info(`Valet Mode Updated: ${JSON.stringify(data.state.valetMode)}`);
          }
        });
      } catch (e) {
        console.error('Global Valet Mode Connection Error', e);
      }
    };
    if (serialNo && accessKeyId) initValetMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialNo]);

  const navigate = useNavigate();
  const location = useLocation();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const handleMenuClick = (event) => {
    navigate(event.key);
    if (isMobile) setDrawerOpen(false);
  };

  const handleToggle = () => {
    if (isMobile) setDrawerOpen(true);
    else setCollapsed((c) => !c);
  };

  const sharedMenu = (
    <Menu
      theme="light"
      mode="inline"
      onSelect={handleMenuClick}
      defaultSelectedKeys={[location.pathname]}
      selectedKeys={[location.pathname]}
      style={{ border: 'none', paddingTop: 8 }}
      items={menuItems}
    />
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Mobile: slide-over Drawer */}
      {isMobile && (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={220}
          styles={{ header: { display: 'none' }, body: { padding: 0 } }}
          closable={false}
        >
          <BrandMark collapsed={false} />
          {sharedMenu}
        </Drawer>
      )}

      {/* Desktop: collapsible Sider */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          theme="light"
          style={{
            borderRight: '1px solid #e2e8f0',
            overflow: 'auto',
            height: '100vh',
            position: 'sticky',
            top: 0,
            left: 0,
          }}
        >
          <BrandMark collapsed={collapsed} />
          {sharedMenu}
        </Sider>
      )}

      <Layout>
        <Header
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: 0,
            background: colorBgContainer,
            borderBottom: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          }}
        >
          <Button
            type="text"
            icon={!isMobile && !collapsed ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
            onClick={handleToggle}
            style={{ fontSize: '16px', width: 64, height: 64, flexShrink: 0 }}
          />
          <div
            style={{
              display: 'flex',
              width: '100%',
              minWidth: 0,
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0 16px 0 4px',
              gap: 8,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              <Text
                ellipsis
                style={{
                  fontSize: isMobile ? 14 : 16,
                  fontWeight: 600,
                  color: '#0f172a',
                  flexShrink: 0,
                }}
              >
                {isMobile ? 'Cloud Simulator' : 'Cloud Service Simulator'}
              </Text>
              <Tooltip
                title={
                  accessKeyId
                    ? 'STS Token active — simulator ready'
                    : 'No STS Token — configure settings to connect'
                }
                placement="bottom"
                color="#1e293b"
              >
                <StatusBadge connected={!!accessKeyId} />
              </Tooltip>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {!isMobile && serialNo && (
                <Text
                  style={{
                    fontSize: 12,
                    color: '#94a3b8',
                    fontFamily: 'monospace',
                    background: '#f1f5f9',
                    padding: '3px 8px',
                    borderRadius: 4,
                    border: '1px solid #e2e8f0',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {serialNo}
                </Text>
              )}
              <Settings accessKeyId={accessKeyId} />
            </div>
          </div>
        </Header>

        <Content
          style={{
            margin: isMobile ? '12px 10px' : '20px 16px',
            padding: isMobile ? 14 : 20,
            minHeight: 'calc(100vh - 160px)',
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}
        >
          {children}
        </Content>

        <Footer
          style={{
            padding: '12px 20px',
            borderTop: '1px solid #e2e8f0',
            background: '#f8fafc',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 4,
            }}
          >
            <Text style={{ fontSize: 12, color: '#94a3b8' }}>
              Visteon Corp. © {new Date().getFullYear()}
            </Text>
            {serialNo && (
              <Text code style={{ fontSize: 11, color: '#94a3b8' }}>
                Serial: {serialNo}
              </Text>
            )}
          </div>
        </Footer>
      </Layout>
    </Layout>
  );
};

export default Navigation;
